import { sendEmailOtp, verifyEmailOtp } from '@/domains/auth/services/auth.service';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { useState } from 'react';

export interface EmailOtpState {
  sent: boolean;
  verified: boolean;
  loading: boolean;
  code: string;
  hint: string;
  verificationToken: string;
  setCode: (c: string) => void;
  send: (email: string) => Promise<string | null>;
  verify: (email: string) => Promise<{ error: string } | { token: string }>;
  reset: () => void;
}

export function useEmailOtp(
  purpose: 'registration' | 'account' = 'registration',
): EmailOtpState {
  const [sent, setSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [hint, setHint] = useState('');
  const [verificationToken, setToken] = useState('');

  const reset = () => {
    setSent(false);
    setVerified(false);
    setCode('');
    setHint('');
    setToken('');
  };

  const send = async (email: string): Promise<string | null> => {
    setLoading(true);
    setHint('');
    try {
      const data = await sendEmailOtp(email, purpose);
      setSent(true);
      setVerified(false);
      setToken('');
      setCode('');
      // Ne jamais afficher le code OTP hors environnement de développement local
      const debug =
        import.meta.env.DEV && data.debug_otp_code
          ? ` (dev: ${data.debug_otp_code})`
          : '';
      setHint(`Code envoyé à ${email}${debug}`);
      return null;
    } catch (e: unknown) {
      return getApiErrorMessage(e, "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (
    email: string,
  ): Promise<{ error: string } | { token: string }> => {
    if (code.length !== 6) return { error: 'Le code OTP doit contenir 6 chiffres.' };
    setLoading(true);
    try {
      const data = await verifyEmailOtp(email, code);
      setVerified(true);
      setToken(data.email_verification_token);
      setHint('E-mail vérifié avec succès ✓');
      return { token: data.email_verification_token };
    } catch (e: unknown) {
      return { error: getApiErrorMessage(e, 'Code invalide.') };
    } finally {
      setLoading(false);
    }
  };

  return { sent, verified, loading, code, hint, verificationToken, setCode, send, verify, reset };
}
